/**
 * Taxonomy geopolitica dell'Osservatorio: 5 macro-aree, ciascuna
 * suddivisa in sotto-regioni con un set di paesi membri.
 *
 * Modello a tre livelli:
 *   1. MACRO_REGIONS  → /regioni            (indice)
 *   2. SUB_REGIONS    → /regioni/[macro]    (griglia sotto-regioni)
 *   3. countrySlugs   → /paesi/[slug]       (scheda paese, già esistente)
 *
 * La sotto-regione aggrega i dispacci di tutti i suoi paesi via
 * `listCases({ countrySlugs: sub.countrySlugs })`. Lo slug del paese
 * deve combaciare con quello memorizzato su Strapi (lib/slug.ts
 * applicato al nome italiano del paese).
 */

import { slugify } from "@/lib/slug";

export type MacroRegion = {
  slug: string;
  name: string;
  eyebrow: string;
  /** Titolo grosso nel frontespizio della pagina /regioni/[macro]. */
  title: string;
  /** Paragrafo sotto al titolo, 1-2 frasi. */
  subtitle: string;
  /** Etichetta breve per la card nell'indice /regioni. */
  short: string;
  /** Colore tinta della card. */
  tone: string;
  /** Simbolo unicode usato come watermark/glyph del frontespizio. */
  symbol: string;
};

export type SubRegion = {
  slug: string;
  name: string;
  /** Slug della macro genitore. */
  macroSlug: string;
  tone: string;
  /** Etichetta sotto al nome nella card (es. "Adriatico orientale"). */
  short: string;
  /** Frase di hook per la card e l'eyebrow. */
  hook: string;
  /** Nomi italiani dei paesi membri (li slugifichiamo a runtime per
   *  passarli a listCases). Mantenere il nome italiano serve anche
   *  per renderizzare i chip nella scheda della sotto-regione. */
  countries: string[];
};

/* ───────────────────────────────────────────────────────────────
   MACRO-AREE — il primo livello di /regioni
   ─────────────────────────────────────────────────────────────── */

export const MACRO_REGIONS: MacroRegion[] = [
  {
    slug: "europa",
    name: "Europa",
    eyebrow: "Macro-area · Europa",
    title: "L'Europa che ci circonda.",
    subtitle:
      "Quattro blocchi geopolitici dell'Europa estesa: Balcani, Mediterraneo meridionale, fianco orientale, cerniera eurasiatica turco-caucasica.",
    short: "27 + UE allargata · Adriatico · Mar Nero",
    tone: "#2a4ba0",
    symbol: "✪",
  },
  {
    slug: "africa",
    name: "Africa",
    eyebrow: "Macro-area · Africa",
    title: "L'Africa che fornisce e che cresce.",
    subtitle:
      "Sei cinture: dal Magreb alla Valle del Nilo, dal Sahel post-coloniale al Corno d'Africa sul Mar Rosso, dal Golfo di Guinea fino all'Africa australe delle materie prime critiche.",
    short: "Magreb · Sahel · Corno d'Africa",
    tone: "#a8742a",
    symbol: "◈",
  },
  {
    slug: "asia",
    name: "Asia",
    eyebrow: "Macro-area · Asia",
    title: "L'Asia delle catene di valore.",
    subtitle:
      "Dal Medio Oriente del greggio all'Asia orientale dei chip, passando per Asia Centrale, Subcontinente indiano e ASEAN.",
    short: "Medio Oriente · Indo-Pacifico",
    tone: "#8e4d3a",
    symbol: "✦",
  },
  {
    slug: "americhe",
    name: "Americhe",
    eyebrow: "Macro-area · Americhe",
    title: "Le Americhe da contare.",
    subtitle:
      "Nord America come alleato, Centro America come passaggio, Sud America come fornitore di materie prime e secondo motore BRICS.",
    short: "USA · LatAm · Caraibi",
    tone: "#6c4cb6",
    symbol: "◉",
  },
  {
    slug: "oceania",
    name: "Oceania",
    eyebrow: "Macro-area · Oceania",
    title: "Il fianco indo-pacifico.",
    subtitle:
      "Australia, Nuova Zelanda e isole pacifiche: AUKUS, terre rare, presenza cinese.",
    short: "Pacifico Sud · AUKUS",
    tone: "#2c7c7c",
    symbol: "⬡",
  },
];

/* ───────────────────────────────────────────────────────────────
   SOTTO-REGIONI — il secondo livello, raggruppate per macro
   ─────────────────────────────────────────────────────────────── */

export const SUB_REGIONS: SubRegion[] = [
  // ── Europa ──────────────────────────────────────────────────
  {
    slug: "balcani",
    name: "Balcani",
    macroSlug: "europa",
    tone: "#8e4d3a",
    short: "Adriatico orientale · Mar Egeo",
    hook: "Sette Stati post-jugoslavi più Albania e Grecia. L'arco che entra nell'UE.",
    countries: [
      "Albania",
      "Bosnia ed Erzegovina",
      "Croazia",
      "Grecia",
      "Kosovo",
      "Macedonia del Nord",
      "Montenegro",
      "Serbia",
      "Slovenia",
    ],
  },
  {
    slug: "europa-meridionale",
    name: "Europa meridionale",
    macroSlug: "europa",
    tone: "#a8742a",
    short: "Bacino del Mediterraneo · UE",
    hook: "La sponda nord del Mediterraneo.",
    countries: ["Italia", "Spagna", "Portogallo", "Francia", "Malta", "Cipro"],
  },
  {
    slug: "europa-orientale",
    name: "Europa orientale",
    macroSlug: "europa",
    tone: "#2a4ba0",
    short: "UE dell'Est · Mar Baltico",
    hook: "Il fianco orientale della NATO.",
    countries: [
      "Polonia",
      "Ucraina",
      "Bielorussia",
      "Moldavia",
      "Ungheria",
      "Slovacchia",
      "Repubblica Ceca",
      "Romania",
      "Bulgaria",
      "Estonia",
      "Lettonia",
      "Lituania",
    ],
  },
  {
    slug: "eurasia",
    name: "Eurasia",
    macroSlug: "europa",
    tone: "#b03935",
    short: "Russia · Turchia · Caucaso",
    hook: "Mosca, Ankara, Caucaso meridionale.",
    countries: ["Russia", "Turchia", "Armenia", "Azerbaigian", "Georgia"],
  },

  // ── Africa ──────────────────────────────────────────────────
  {
    slug: "magreb",
    name: "Magreb",
    macroSlug: "africa",
    tone: "#a8742a",
    short: "Sponda sud del Mediterraneo",
    hook: "La sponda sud del Mediterraneo occidentale.",
    // I cinque membri dell'Unione del Magreb Arabo (Marrakech, 1989).
    // Qui prima c'era l'Egitto, che non c'entra: "magreb" è il ponente
    // arabo, l'Egitto è "mashreq", il levante. E mancava la Mauritania,
    // che dell'Unione è membro fondatore; stava nel Sahel, dove pure ci
    // starebbe, ma il Magreb ha una definizione formale e il Sahel no.
    countries: ["Marocco", "Algeria", "Tunisia", "Libia", "Mauritania"],
  },
  {
    // Nata per raccogliere Egitto e Sudan, che prima stavano l'uno nel
    // Magreb (dove non c'entra: quello è il ponente arabo, l'Egitto è
    // levante) e l'altro nel Sahel (dove ci sta per la fascia
    // saheliana, ma non per il Nilo, Suez e il Mar Rosso, che sono la
    // parte di cui si parla nei dispacci).
    slug: "valle-nilo",
    name: "Valle del Nilo",
    macroSlug: "africa",
    tone: "#2a4ba0",
    short: "Nilo · Suez · Mar Rosso",
    hook: "Il canale, la diga, il grano.",
    countries: ["Egitto", "Sudan"],
  },
  {
    slug: "sahel",
    name: "Sahel",
    macroSlug: "africa",
    tone: "#8e4d3a",
    short: "Fascia subsahariana",
    hook: "Golpe, terrorismo, oro: la fascia più instabile del continente.",
    countries: ["Mali", "Niger", "Burkina Faso", "Ciad"],
  },
  {
    slug: "corno-africa",
    name: "Corno d'Africa",
    macroSlug: "africa",
    tone: "#2c7c7c",
    short: "Mar Rosso · Oceano Indiano",
    hook: "Mar Rosso, houthi, basi militari: il collo di bottiglia del commercio.",
    countries: ["Etiopia", "Eritrea", "Somalia", "Gibuti", "Kenya"],
  },
  {
    slug: "africa-occidentale",
    name: "Africa occidentale",
    macroSlug: "africa",
    tone: "#3e7c4b",
    short: "Golfo di Guinea · ECOWAS",
    hook: "Petrolio, cacao, oro e l'integrazione ECOWAS.",
    countries: [
      "Nigeria",
      "Ghana",
      "Costa d'Avorio",
      "Senegal",
      "Guinea",
      "Liberia",
      "Sierra Leone",
    ],
  },
  {
    slug: "africa-australe",
    name: "Africa australe",
    macroSlug: "africa",
    tone: "#6c4cb6",
    short: "Sud del continente",
    hook: "Materie prime critiche e l'asse BRICS.",
    countries: [
      "Sudafrica",
      "Mozambico",
      "Angola",
      "Zambia",
      "Zimbabwe",
      "Tanzania",
      "Namibia",
      "Botswana",
    ],
  },

  // ── Asia ────────────────────────────────────────────────────
  {
    slug: "medio-oriente",
    name: "Medio Oriente",
    macroSlug: "asia",
    tone: "#8e4d3a",
    short: "Golfo Persico · Levante",
    hook: "Greggio, gas, conflitti aperti.",
    countries: [
      "Iran",
      "Arabia Saudita",
      "Israele",
      "Emirati Arabi Uniti",
      "Iraq",
      "Siria",
      "Libano",
      "Giordania",
      "Qatar",
      "Kuwait",
      "Yemen",
      "Oman",
      "Bahrein",
    ],
  },
  {
    slug: "asia-centrale",
    name: "Asia Centrale",
    macroSlug: "asia",
    tone: "#a8742a",
    short: "Cinque '-stan' del Caspio",
    hook: "Kazakhstan, Uzbekistan, Turkmenistan e i corridoi trans-caspici.",
    countries: [
      "Kazakistan",
      "Uzbekistan",
      "Turkmenistan",
      "Kirghizistan",
      "Tagikistan",
      "Afghanistan",
    ],
  },
  {
    slug: "asia-meridionale",
    name: "Asia Meridionale",
    macroSlug: "asia",
    tone: "#b03935",
    short: "Subcontinente indiano",
    hook: "1,8 miliardi di persone.",
    countries: [
      "India",
      "Pakistan",
      "Bangladesh",
      "Sri Lanka",
      "Nepal",
      "Bhutan",
      "Maldive",
    ],
  },
  {
    slug: "asia-orientale",
    name: "Asia Orientale",
    macroSlug: "asia",
    tone: "#2a4ba0",
    short: "Pacifico nord",
    hook: "La fabbrica e i chip del mondo.",
    countries: [
      "Cina",
      "Giappone",
      "Corea del Sud",
      "Corea del Nord",
      "Taiwan",
      "Mongolia",
    ],
  },
  {
    slug: "sudest-asiatico",
    name: "Sud-est asiatico",
    macroSlug: "asia",
    tone: "#3e7c4b",
    short: "ASEAN",
    hook: "Il nuovo polo manifatturiero.",
    countries: [
      "Vietnam",
      "Indonesia",
      "Filippine",
      "Thailandia",
      "Malesia",
      "Singapore",
      "Myanmar",
      "Cambogia",
      "Laos",
      "Brunei",
      "Timor Est",
    ],
  },

  // ── Americhe ────────────────────────────────────────────────
  {
    slug: "nord-america",
    name: "Nord America",
    macroSlug: "americhe",
    tone: "#2a4ba0",
    short: "USA · Canada",
    hook: "Alleati storici, mercato di sbocco principale.",
    countries: ["Stati Uniti d'America", "Canada"],
  },
  {
    slug: "centro-america",
    name: "Centro America",
    macroSlug: "americhe",
    tone: "#a8742a",
    short: "Messico · Caraibi",
    hook: "Nearshoring, migrazione, USMCA.",
    countries: [
      "Messico",
      "Guatemala",
      "Honduras",
      "El Salvador",
      "Nicaragua",
      "Costa Rica",
      "Panama",
      "Cuba",
      "Repubblica Dominicana",
      "Haiti",
    ],
  },
  {
    slug: "sud-america",
    name: "Sud America",
    macroSlug: "americhe",
    tone: "#3e7c4b",
    short: "Cono Sud · Andini",
    hook: "Materie prime, BRICS, LatAm.",
    countries: [
      "Brasile",
      "Argentina",
      "Cile",
      "Perù",
      "Colombia",
      "Venezuela",
      "Uruguay",
      "Paraguay",
      "Bolivia",
      "Ecuador",
    ],
  },

  // ── Oceania ─────────────────────────────────────────────────
  {
    slug: "pacifico-sud",
    name: "Pacifico del Sud",
    macroSlug: "oceania",
    tone: "#2c7c7c",
    short: "Australia · Nuova Zelanda · isole",
    hook: "AUKUS, materie prime critiche.",
    countries: [
      "Australia",
      "Nuova Zelanda",
      "Papua Nuova Guinea",
      "Figi",
      "Isole Salomone",
    ],
  },
];

/* ───────────────────────────────────────────────────────────────
   HELPER
   ─────────────────────────────────────────────────────────────── */

export function getMacroBySlug(slug: string): MacroRegion | undefined {
  return MACRO_REGIONS.find((m) => m.slug === slug);
}

export function getSubRegionBySlug(slug: string): SubRegion | undefined {
  return SUB_REGIONS.find((s) => s.slug === slug);
}

export function getSubRegionsByMacro(macroSlug: string): SubRegion[] {
  return SUB_REGIONS.filter((s) => s.macroSlug === macroSlug);
}

/** Slug paesi (per `listCases({ countrySlugs })`) ricavati dal nome
 *  italiano via la stessa `slugify` usata da Strapi.
 *
 *  ATTENZIONE: gli slug a monte non sono affidabili per interrogare
 *  l'archivio. Dove un paese è stato inserito due volte, il secondo
 *  record porta un suffisso numerico — "marocco-2", "tunisia-2",
 *  "albania-2" — e i dispacci stanno lì. Cercando "marocco" non si
 *  trova niente pur essendocene più di cento. Per il feed delle regioni
 *  si usano i NOMI (`countryNamesFor`), che sono quelli veri.
 *  Questa resta per gli usi in cui lo slug è davvero uno slug, cioè i
 *  link alle schede paese. */
export function countrySlugsFor(sub: SubRegion): string[] {
  return sub.countries.map((c) => slugify(c));
}

/** Nomi paesi di una sotto-regione, per `listCases({ countryTitles })`. */
export function countryNamesFor(sub: SubRegion): string[] {
  return [...sub.countries];
}

/** Tutti gli slug paesi di una macro (unione di tutte le sotto-regioni). */
export function countrySlugsForMacro(macroSlug: string): string[] {
  const subs = getSubRegionsByMacro(macroSlug);
  const all = subs.flatMap((s) => countrySlugsFor(s));
  return Array.from(new Set(all));
}

/**
 * Perché qui NON si aggiungono le voci di macro-area.
 *
 * A monte il campo `countries` contiene anche voci che paesi non sono:
 * "Africa", "Africa Settentrionale", "Europa", "Asia Meridionale". Ho
 * provato a includerle, pensando che ci fossero dispacci raggiungibili
 * solo così, e ho misurato: sull'Africa aggiungono 5 dispacci su 1.514,
 * perché ogni dispaccio porta già anche il suo paese specifico. Sul-
 * l'Europa invece ne aggiungerebbero 100.901, cioè quasi tutto
 * l'archivio, e la pagina della macro-area diventerebbe il rullo.
 * Cinque dispacci in più non valgono quel prezzo.
 */
/** Tutti i nomi paesi di una macro. */
export function countryNamesForMacro(macroSlug: string): string[] {
  const subs = getSubRegionsByMacro(macroSlug);
  return Array.from(new Set(subs.flatMap((s) => s.countries)));
}

/** Reverse lookup: data una slug-paese ("albania", "bosnia-erzegovina",
 *  …), restituisce la sotto-regione di appartenenza, o undefined. */
export function findSubRegionForCountrySlug(
  countrySlug: string
): SubRegion | undefined {
  return SUB_REGIONS.find((s) =>
    s.countries.some((c) => slugify(c) === countrySlug)
  );
}

/** Restituisce il nome italiano canonico di un paese dato il suo slug,
 *  attingendo dalla taxonomy. Usato come fallback per renderizzare la
 *  scheda paese anche quando Strapi non ha quel record. */
export function countryNameForSlug(countrySlug: string): string | undefined {
  for (const s of SUB_REGIONS) {
    for (const c of s.countries) {
      if (slugify(c) === countrySlug) return c;
    }
  }
  return undefined;
}

/** Paesi "vicini" (stessa sotto-regione) escluso il paese stesso, usati
 *  per renderizzare la pillola "Esplora i paesi vicini" in fondo alla
 *  scheda paese. */
export function siblingCountriesForSlug(countrySlug: string): string[] {
  const sub = findSubRegionForCountrySlug(countrySlug);
  if (!sub) return [];
  return sub.countries.filter((c) => slugify(c) !== countrySlug);
}

/* ───────────────────────────────────────────────────────────────
   Filtro "vero paese" per la pagina /paesi
   ─────────────────────────────────────────────────────────────── */

/** Nomi che NON sono paesi e vanno esclusi dalla vetrina /paesi:
 *   - tutte le macro-aree (Europa, Africa, Asia, Americhe, Oceania)
 *   - tutte le sotto-regioni (Balcani, Magreb, Levante, …)
 *   - unioni politiche e categorie geografiche generiche
 *
 *  Lower-cased per match insensibile al case. Set per O(1). */
const NON_COUNTRY_NAMES = new Set<string>(
  [
    ...MACRO_REGIONS.map((m) => m.name),
    ...SUB_REGIONS.map((s) => s.name),
    "UE",
    "Unione Europea",
    "Europa",
    "Mondo",
    "Internazionale",
    "Globale",
    "Mediterraneo",
    "Mar Mediterraneo",
    "Mar Nero",
    "Mar Rosso",
    "Mar Baltico",
    "Mar Caspio",
    "Atlantico",
    "Pacifico",
    "Oceano Indiano",
    "Indo-Pacifico",
    "Levante",
    "Caucaso",
    "Golfo Persico",
    "Golfo di Guinea",
    "Penisola Arabica",
    "Subcontinente indiano",
    "Africa subsahariana",
    "Asia-Pacifico",
    "BRICS",
    "G7",
    "G20",
    "NATO",
    "OPEC",
    "ASEAN",
    "ECOWAS",
    // Aree geografiche che a monte hanno un nome diverso da quello
    // della tassonomia qui sopra, e che quindi il match per nome non
    // intercettava: "Africa Settentrionale" qui si chiama Magreb e
    // Valle del Nilo, "Asia Occidentale" è il Medio Oriente, "Sud Est
    // Europa" sono i Balcani. Sono i nomi che l'archivio usa davvero
    // (verificati sulle 96 voci dell'endpoint /countries), non ipotesi:
    // senza queste righe finivano nell'indice dei paesi accanto ad
    // Albania e Marocco.
    "Africa Settentrionale",
    "Africa Orientale",
    "Asia Occidentale",
    "America del Nord",
    "America del Sud",
    "Sud Est Europa",
  ].map((s) => s.toLowerCase())
);

/** True se il nome corrisponde a un paese, false se è una regione,
 *  un mare, un'organizzazione o un'aggregazione geografica. */
export function isStrictCountry(name: string): boolean {
  if (!name) return false;
  return !NON_COUNTRY_NAMES.has(name.trim().toLowerCase());
}
